import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tableQrManagerApi } from '../utils/tableQrApi';
import { composeTableQrPng } from '../utils/tableQrImage';

const unwrapQrLink = (payload = {}) => payload.qrLink || payload.link || payload.tableQr || payload.qr || null;

const resolvePublicUrl = (qrLink) => qrLink?.publicUrl || qrLink?.url || qrLink?.menuUrl || '';
const resolveImageUrl = (qrLink) => qrLink?.qrCodeDataUrl || qrLink?.imageDataUrl || qrLink?.imageUrl || '';

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const input = document.createElement('textarea');
  input.value = value;
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.select();
  document.execCommand('copy');
  document.body.removeChild(input);
};

const TableQrDialog = ({ open, table, onClose }) => {
  const [qrLink, setQrLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [composedBlob, setComposedBlob] = useState(null);
  const tableImageLabel = useMemo(() => ({ number: table?.number, name: table?.name }), [table?.name, table?.number]);

  const loadQr = useCallback(async () => {
    if (!open || !table?.id) return;
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const data = await tableQrManagerApi.get(table.id);
      setQrLink(unwrapQrLink(data));
    } catch (err) {
      if (err.status === 404) setQrLink(null);
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [open, table?.id]);

  useEffect(() => {
    if (open) loadQr();
    else {
      setQrLink(null);
      setComposedBlob(null);
      setError('');
      setMessage('');
    }
  }, [loadQr, open]);

  useEffect(() => {
    let objectUrl = '';
    let active = true;
    const loadPreview = async () => {
      if (!open || !table?.id || !qrLink) return;
      try {
        const rawImageUrl = resolveImageUrl(qrLink);
        const rawBlob = rawImageUrl
          ? await fetch(rawImageUrl).then((response) => response.blob())
          : await tableQrManagerApi.download(table.id);
        const blob = await composeTableQrPng(rawBlob, tableImageLabel);
        objectUrl = URL.createObjectURL(blob);
        if (active) {
          setComposedBlob(blob);
          setPreviewUrl(objectUrl);
        }
      } catch {
        if (active) {
          setComposedBlob(null);
          setPreviewUrl('');
        }
      }
    };
    setPreviewUrl('');
    setComposedBlob(null);
    loadPreview();
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, qrLink, table?.id, tableImageLabel]);

  const runAction = async (name, handler, successMessage) => {
    setAction(name);
    setError('');
    setMessage('');
    try {
      const data = await handler();
      const nextQrLink = unwrapQrLink(data);
      if (nextQrLink) setQrLink(nextQrLink);
      setMessage(successMessage);
    } catch (err) {
      setError(err.message);
    } finally {
      setAction('');
    }
  };

  const publicUrl = useMemo(() => resolvePublicUrl(qrLink), [qrLink]);
  const imageUrl = useMemo(() => previewUrl || resolveImageUrl(qrLink), [previewUrl, qrLink]);
  const enabled = qrLink?.enabled !== false && qrLink?.active !== false;

  const handleCopy = async () => {
    try {
      await copyText(publicUrl);
      setMessage('Link da mesa copiado.');
      setError('');
    } catch {
      setError('Não foi possível copiar o link.');
    }
  };

  const handleDownload = async () => {
    setAction('download');
    setError('');
    try {
      let blob = composedBlob;
      if (!blob) {
        const rawBlob = imageUrl.startsWith('data:')
          ? await fetch(imageUrl).then((response) => response.blob())
          : await tableQrManagerApi.download(table.id);
        blob = await composeTableQrPng(rawBlob, table);
      }
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = `qr-mesa-${String(table.number || table.id).replace(/[^a-z0-9_-]/gi, '-')}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
      setMessage('QR Code baixado.');
    } catch (err) {
      setError(err.message);
    } finally {
      setAction('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCode2Icon /> QR da mesa {table?.number}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
            <CircularProgress size={34} />
            <Typography color="text.secondary">Carregando QR Code...</Typography>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {message ? <Alert severity="success">{message}</Alert> : null}
            {!qrLink ? (
              <Alert severity="info">
                Esta mesa ainda não possui QR Code. Ao gerar, o link ficará vinculado exclusivamente à mesa {table?.number}.
              </Alert>
            ) : (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 224,
                      height: 224,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: '#fff',
                      p: 1,
                    }}
                  >
                    {imageUrl ? (
                      <Box component="img" src={imageUrl} alt={`QR Code da mesa ${table?.number}`} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <QrCode2Icon sx={{ fontSize: 150, color: 'text.disabled' }} />
                    )}
                  </Box>
                  <Stack spacing={1.25} sx={{ flex: 1, width: '100%' }}>
                    <Typography variant="subtitle1" fontWeight={900}>Mesa {table?.number}</Typography>
                    {table?.name ? <Typography color="text.secondary">{table.name}</Typography> : null}
                    <Alert severity={enabled ? 'success' : 'warning'} icon={false}>
                      {enabled ? 'QR Code ativo para novos pedidos.' : 'QR Code desativado.'}
                    </Alert>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={enabled}
                          disabled={Boolean(action)}
                          onChange={(event) => runAction(
                            'toggle',
                            () => tableQrManagerApi.setEnabled(table.id, event.target.checked),
                            event.target.checked ? 'QR Code ativado.' : 'QR Code desativado.'
                          )}
                        />
                      )}
                      label="Aceitar pedidos por este QR Code"
                    />
                  </Stack>
                </Stack>
                <TextField
                  fullWidth
                  label="Link exclusivo da mesa"
                  value={publicUrl}
                  InputProps={{ readOnly: true }}
                  helperText="O link não contém CNPJ ou identificador interno da empresa."
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy} disabled={!publicUrl || Boolean(action)}>
                    Copiar link
                  </Button>
                  <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={!enabled || Boolean(action)}>
                    Baixar PNG
                  </Button>
                </Stack>
                <Divider />
                <Box>
                  <Typography fontWeight={900}>Trocar o QR Code</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Use somente se a placa for perdida ou o link precisar ser invalidado. A imagem anterior deixará de aceitar novos acessos.
                  </Typography>
                  <Button
                    color="warning"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    disabled={Boolean(action)}
                    onClick={() => {
                      if (window.confirm(`Gerar um novo QR Code para a mesa ${table?.number}? O anterior será invalidado.`)) {
                        runAction('rotate', () => tableQrManagerApi.rotate(table.id), 'Novo QR Code gerado.');
                      }
                    }}
                  >
                    Rotacionar QR Code
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {!loading && !qrLink ? (
          <Button
            variant="contained"
            startIcon={<QrCode2Icon />}
            disabled={Boolean(action)}
            onClick={() => runAction('create', () => tableQrManagerApi.create(table.id), 'QR Code gerado para esta mesa.')}
          >
            Gerar QR Code
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default TableQrDialog;
